/**
 * Settings route.
 *
 * Two areas:
 *  1. Microsoft 365 connection — the backend OAuth flow that grants delegated
 *     Graph access (read the Outlook calendar, send on Teams). Connection state
 *     is read from `users/{uid}.microsoft`; connecting redirects to Microsoft via
 *     `auth.connectMicrosoft()`, disconnecting calls the `disconnectMicrosoft`
 *     callable. The `?connected=1` return param surfaces a success notice.
 *  2. Preferences — the proactive routine toggles, quiet hours, and timezone,
 *     persisted to `users/{uid}.preferences` via a merged Firestore write.
 *
 * Kept mounted at path '/settings'. Exported as `SettingsRoute`.
 */
import type {
  AgentLanguage,
  BriefingRoutineKey,
  ConnectedAccountMeta,
  ConnectedAccountSlotId,
  MicrosoftIntegrationMeta,
  RoutineType,
  UserDoc,
  UserFeatureFlags,
  UserPreferences,
} from '@agente/shared'
import {
  DEFAULT_LIVE_VOICE,
  DEFAULT_TIMEZONE,
  getPersonaForVoice,
  LIVE_MODEL_OPTIONS,
  LIVE_VOICE_OPTIONS,
  MAX_MS_EXTRA_ACCOUNTS,
  memoriesRequireApproval,
  missingRequiredScopes,
  resolveChannels,
} from '@agente/shared'
import { Link } from '@tanstack/react-router'
import {
  Check,
  ChevronDown,
  Link2,
  Loader,
  LogOut,
  MessageCircle,
  Send,
  ShieldCheck,
  Unlink,
} from 'lucide-react'
import { type ComponentType, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { useUserDoc } from '@/hooks/useUserDoc'
import { useAuth } from '@/lib/auth'
import {
  disconnectConnectedAccount,
  disconnectMicrosoft,
  getGoogleAuthUrl,
  getMicrosoftExtraAuthUrl,
  linkWhatsapp,
  sendChannelTest,
  unlinkWhatsapp,
} from '@/lib/callables'
import { updateUserPreferences } from '@/lib/firestore'

/** Feature flag da conversa por voz (baked pelo Vite em build time). */
const VOICE_ENABLED = import.meta.env.VITE_VOICE_ENABLED === 'true'

const LANGUAGE_OPTIONS: Array<{ value: AgentLanguage; label: string }> = [
  { value: 'pt-BR', label: 'Português (Brasil)' },
  { value: 'en-US', label: 'Inglês (EUA)' },
]

/** Microsoft logo mark (inline SVG). */
function MicrosoftMark() {
  return (
    <svg aria-hidden="true" viewBox="0 0 21 21" className="size-4" focusable="false">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  )
}

/** Google logo mark (inline SVG). */
function GoogleMark() {
  return (
    <svg aria-hidden="true" viewBox="0 0 18 18" className="size-4" focusable="false">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.9c1.7-1.57 2.7-3.87 2.7-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.54-1.84.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.9v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.95 10.7A5.4 5.4 0 0 1 3.66 9c0-.59.1-1.17.29-1.7V4.97H.9A9 9 0 0 0 0 9c0 1.45.35 2.83.9 4.03l3.05-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .9 4.97l3.05 2.33C4.66 5.17 6.65 3.58 9 3.58Z"
      />
    </svg>
  )
}

/** Rótulo pt-BR e ícone de cada slot fixo de conta conectada. */
const SLOT_LABELS: Record<ConnectedAccountSlotId, { title: string; mark: ComponentType }> = {
  'google-1': { title: 'Google', mark: GoogleMark },
  'ms-extra-1': { title: 'Microsoft (conta adicional 1)', mark: MicrosoftMark },
  'ms-extra-2': { title: 'Microsoft (conta adicional 2)', mark: MicrosoftMark },
  'ms-extra-3': { title: 'Microsoft (conta adicional 3)', mark: MicrosoftMark },
}

const MS_EXTRA_SLOT_IDS: ConnectedAccountSlotId[] = ['ms-extra-1', 'ms-extra-2', 'ms-extra-3']

/** Mensagens pt-BR para os códigos de erro devolvidos pelos callbacks OAuth via `?error=`. */
const CONNECTED_ACCOUNT_ERROR_MESSAGES: Record<string, string> = {
  google_consent_failed: 'A conexão com o Google foi cancelada ou negada.',
  ms_extra_consent_failed: 'A conexão com a conta Microsoft adicional foi cancelada ou negada.',
  missing_code_or_state: 'A conexão não pôde ser concluída. Tente novamente.',
  invalid_state_purpose: 'A conexão não pôde ser concluída. Tente novamente.',
  conta_deve_ser_externa:
    'Essa conta pertence ao tenant da PG Advogados. Contas adicionais precisam ser de uma organização externa.',
  limite_de_contas_atingido: 'Limite de contas Microsoft adicionais atingido (máximo de 3).',
  feature_desabilitada: 'Esse recurso ainda não foi habilitado para o seu usuário.',
  connect_failed: 'Não foi possível concluir a conexão. Tente novamente.',
}

function connectedAccountErrorMessage(reason: string): string {
  return (
    CONNECTED_ACCOUNT_ERROR_MESSAGES[reason] ??
    'Não foi possível concluir a conexão. Tente novamente.'
  )
}

const ROUTINE_LABELS: Array<{ key: RoutineType; title: string; hint: string }> = [
  {
    key: 'eveningBriefing',
    title: 'Resumo às 20h',
    hint: 'Panorama do dia seguinte no fim da tarde.',
  },
  {
    key: 'morningBriefing',
    title: 'Lembrete às 7h',
    hint: 'Compromissos e prioridades logo pela manhã.',
  },
  {
    key: 'hourlyFollowup',
    title: 'Follow-up de hora em hora',
    hint: 'Acompanhamento das próximas reuniões ao longo do dia.',
  },
  {
    key: 'emailReminders',
    title: 'Lembretes de e-mails',
    hint: 'Lê os e-mails uma vez por dia e sugere lembretes; as sugestões precisam ser aceitas na página Lembretes.',
  },
]

/** Campos de instruções personalizadas por rotina de briefing (exclui emailReminders). */
const BRIEFING_INSTRUCTION_LABELS: Array<{
  key: BriefingRoutineKey
  label: string
  placeholder: string
}> = [
  {
    key: 'morningBriefing',
    label: 'Instruções para o resumo da manhã',
    placeholder: 'Ex.: destaque reuniões com clientes e avise se houver conflito de horário.',
  },
  {
    key: 'eveningBriefing',
    label: 'Instruções para o resumo da noite',
    placeholder: 'Ex.: priorize os compromissos externos e mencione deslocamentos longos.',
  },
  {
    key: 'hourlyFollowup',
    label: 'Instruções para o follow-up de hora em hora',
    placeholder: 'Ex.: seja objetivo e cite apenas o próximo compromisso relevante.',
  },
]

/** Default preferences used until the user document loads (or for new users). */
const DEFAULT_PREFERENCES: UserPreferences = {
  timezone: DEFAULT_TIMEZONE,
  routines: {
    eveningBriefing: false,
    morningBriefing: false,
    hourlyFollowup: false,
    emailReminders: false,
  },
  quietHours: { start: '21:00', end: '07:00' },
  deliveryChannel: 'teams',
}

export function SettingsRoute() {
  const { userDoc, loading } = useUserDoc()
  const microsoft = userDoc?.microsoft

  // Show the "conectado"/erro notice once when we return from any OAuth callback
  // (Microsoft primária, Google ou Microsoft extra — todas retornam para /settings
  // com `?connected=...` no sucesso ou `?error=...` na falha).
  const noticeShown = useRef(false)
  useEffect(() => {
    if (noticeShown.current) return
    const params = new URLSearchParams(window.location.search)
    const connected = params.get('connected')
    const error = params.get('error')
    if (connected === '1') {
      noticeShown.current = true
      toast.success('Microsoft 365 conectado', {
        description: 'Agenda e Teams disponíveis para as rotinas.',
      })
    } else if (connected === 'google') {
      noticeShown.current = true
      toast.success('Conta Google conectada', {
        description: 'Agenda e e-mail dessa conta já podem ser usados como fonte de dados.',
      })
    } else if (connected === 'ms-extra') {
      noticeShown.current = true
      toast.success('Conta Microsoft adicional conectada', {
        description: 'Agenda e e-mail dessa conta já podem ser usados como fonte de dados.',
      })
    } else if (error) {
      noticeShown.current = true
      toast.error('Não foi possível concluir a conexão', {
        description: connectedAccountErrorMessage(error),
      })
    }
  }, [])

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 p-4 py-6 md:p-6">
      <header className="space-y-1">
        <h1 className="font-display text-2xl font-semibold tracking-tight">Configurações</h1>
        <p className="text-muted-foreground text-sm">
          Conecte o Microsoft 365 e ajuste as rotinas proativas do Agente Executivo.
        </p>
      </header>

      <MicrosoftCard loading={loading} microsoft={microsoft} />
      <ConnectedAccountsCard
        loading={loading}
        connectedAccounts={userDoc?.connectedAccounts}
        features={userDoc?.features}
      />
      <VoiceCard loading={loading} preferences={userDoc?.preferences} />
      <PreferencesCard
        loading={loading}
        preferences={userDoc?.preferences}
        microsoftConnected={microsoft?.connected === true}
        microsoft={microsoft}
        whatsapp={userDoc?.whatsapp}
        features={userDoc?.features}
      />
      <MemoriesCard loading={loading} preferences={userDoc?.preferences} />
    </div>
  )
}

function MicrosoftCard({
  loading,
  microsoft,
}: {
  loading: boolean
  microsoft: NonNullable<ReturnType<typeof useUserDoc>['userDoc']>['microsoft'] | undefined
}) {
  const { connectMicrosoft } = useAuth()
  const [pending, setPending] = useState<'connect' | 'disconnect' | null>(null)

  const connected = microsoft?.connected === true

  async function handleConnect() {
    setPending('connect')
    try {
      // Redirects the browser to Microsoft; the callback returns to /settings?connected=1.
      await connectMicrosoft()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível iniciar a conexão', { description: message })
      setPending(null)
    }
  }

  async function handleDisconnect() {
    setPending('disconnect')
    try {
      await disconnectMicrosoft()
      toast.success('Microsoft 365 desconectado')
      // The users/{uid}.microsoft snapshot updates itself; just clear the spinner.
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível desconectar', { description: message })
    } finally {
      setPending(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MicrosoftMark />
          Microsoft 365
        </CardTitle>
        <CardDescription>
          Conectar permite ao agente ler sua agenda do Outlook e enviar mensagens no Teams. O
          escritório mantém essas informações confidenciais.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {loading ? (
          <Skeleton className="h-10 w-full" />
        ) : connected ? (
          <>
            <div className="border-border bg-muted/40 flex items-center gap-3 rounded-lg border p-3">
              <ShieldCheck className="text-brand size-5 shrink-0" aria-hidden="true" />
              <div className="min-w-0">
                <p className="text-sm font-medium">Conta conectada</p>
                <p className="text-muted-foreground truncate text-sm">
                  {microsoft?.upn ?? 'conta Microsoft'}
                </p>
              </div>
            </div>
            {microsoft?.reconnectRequired === true ? (
              // Token defasado (escopo novo): a conta segue conectada e os recursos
              // antigos funcionam; é só um nudge para reconectar e habilitar os novos.
              // Lido do flag sticky `reconnectRequired` (não do `status`, que o
              // getAccessToken sobrescreve a cada refresh de token).
              <div className="border-border bg-muted/40 flex flex-col gap-3 rounded-lg border p-3">
                <p className="text-sm">
                  Novos recursos requerem reconectar sua conta Microsoft para habilitá-los.
                </p>
                <div>
                  <Button
                    type="button"
                    onClick={() => void handleConnect()}
                    disabled={pending !== null}
                    className="shadow-sm"
                  >
                    {pending === 'connect' ? (
                      <Loader className="size-4 animate-spin" />
                    ) : (
                      <MicrosoftMark />
                    )}
                    Reconectar Microsoft 365
                  </Button>
                </div>
              </div>
            ) : microsoft?.status === 'error' || microsoft?.status === 'revoked' ? (
              <p className="text-destructive text-sm">
                A conexão precisa de atenção. Reconecte para continuar recebendo as rotinas.
              </p>
            ) : null}
            <div>
              <Button
                type="button"
                variant="outline"
                onClick={() => void handleDisconnect()}
                disabled={pending !== null}
              >
                {pending === 'disconnect' ? (
                  <Loader className="size-4 animate-spin" />
                ) : (
                  <LogOut className="size-4" />
                )}
                Desconectar
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-muted-foreground text-sm">Nenhuma conta conectada.</p>
            <div>
              <Button
                type="button"
                onClick={() => void handleConnect()}
                disabled={pending !== null}
                className="shadow-sm"
              >
                {pending === 'connect' ? (
                  <Loader className="size-4 animate-spin" />
                ) : (
                  <MicrosoftMark />
                )}
                Conectar Microsoft 365
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

const ALL_CONNECTED_ACCOUNT_SLOT_IDS: ConnectedAccountSlotId[] = ['google-1', ...MS_EXTRA_SLOT_IDS]

type ConnectedAccountPendingAction = ConnectedAccountSlotId | 'connect-google' | 'connect-ms-extra'

/**
 * Cartão "Contas conectadas" — contas ADICIONAIS de fonte de dados (1 Google +
 * até 3 Microsoft corporativas extra), nunca a conta de login/primária (essa
 * fica no cartão Microsoft 365 acima). Reaproveita o vocabulário visual de
 * status do `MicrosoftCard` (conectado / reconectar / erro).
 */
function ConnectedAccountsCard({
  loading,
  connectedAccounts,
  features,
}: {
  loading: boolean
  connectedAccounts: UserDoc['connectedAccounts']
  /** Feature por usuário (liberação gradual — ver UserFeatureFlags); ausente ⇒ nenhuma habilitada. */
  features: UserFeatureFlags | undefined
}) {
  const googleAccountFeatureOn = features?.googleAccount === true
  const msExtraAccountsFeatureOn = features?.msExtraAccounts === true
  const [pending, setPending] = useState<ConnectedAccountPendingAction | null>(null)

  // "Ativo" = tem metadados e não está no estado 'disconnected' (que é como uma
  // conta desvinculada continua registrada, só marcada — ver disconnectConnectedAccount).
  const activeSlotIds = ALL_CONNECTED_ACCOUNT_SLOT_IDS.filter((slotId) => {
    const meta = connectedAccounts?.[slotId]
    return meta != null && meta.status !== 'disconnected'
  })
  const googleActive = activeSlotIds.includes('google-1')
  const msExtraActiveCount = activeSlotIds.filter((slotId) => slotId !== 'google-1').length
  const msExtraSlotAvailable = msExtraActiveCount < MAX_MS_EXTRA_ACCOUNTS

  async function handleConnectGoogle() {
    setPending('connect-google')
    try {
      const { data } = await getGoogleAuthUrl({ returnTo: `${window.location.origin}/settings` })
      window.location.assign(data.authUrl)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível iniciar a conexão com o Google', { description: message })
      setPending(null)
    }
  }

  // `targetSlotId` direciona a reconexão para o slot exato do card clicado
  // (Fix 2) — sem ele (botão genérico "Conectar conta Microsoft adicional"),
  // o backend aloca o primeiro slot livre, como antes.
  async function handleConnectMsExtra(targetSlotId?: ConnectedAccountSlotId) {
    setPending('connect-ms-extra')
    try {
      const { data } = await getMicrosoftExtraAuthUrl({
        returnTo: `${window.location.origin}/settings`,
        ...(targetSlotId ? { targetSlotId } : {}),
      })
      window.location.assign(data.authUrl)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível iniciar a conexão com a conta Microsoft adicional', {
        description: message,
      })
      setPending(null)
    }
  }

  function handleReconnect(slotId: ConnectedAccountSlotId) {
    return slotId === 'google-1' ? handleConnectGoogle() : handleConnectMsExtra(slotId)
  }

  async function handleDisconnect(slotId: ConnectedAccountSlotId, title: string) {
    const confirmed = window.confirm(
      `Desvincular a conta ${title}? O agente deixa de usá-la como fonte de dados.`,
    )
    if (!confirmed) return
    setPending(slotId)
    try {
      await disconnectConnectedAccount({ slotId })
      toast.success('Conta desvinculada')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível desvincular a conta', { description: message })
    } finally {
      setPending(null)
    }
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Contas conectadas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Contas conectadas</CardTitle>
        <CardDescription>
          Contas extras são fontes de dados adicionais (agenda e e-mail) para o agente — elas não
          mudam o login nem substituem a conta Microsoft principal, que continua no cartão Microsoft
          365 acima.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {activeSlotIds.length === 0 ? (
          <p className="text-muted-foreground text-sm">Nenhuma conta extra conectada.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {activeSlotIds.map((slotId) => {
              const meta = connectedAccounts?.[slotId] as ConnectedAccountMeta
              const { title, mark: Mark } = SLOT_LABELS[slotId]
              const reconnectNudge = meta.reconnectRequired === true
              const needsAttention =
                reconnectNudge || meta.status === 'error' || meta.status === 'revoked'
              const reconnectAction: ConnectedAccountPendingAction =
                slotId === 'google-1' ? 'connect-google' : 'connect-ms-extra'

              return (
                <div
                  key={slotId}
                  className="border-border bg-muted/40 flex flex-col gap-3 rounded-lg border p-3"
                >
                  <div className="flex items-center gap-3">
                    <Mark />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">{title}</p>
                      <p className="text-muted-foreground truncate text-sm">
                        {meta.email ?? 'conta conectada'}
                      </p>
                    </div>
                    {!needsAttention ? (
                      <ShieldCheck className="text-brand size-5 shrink-0" aria-hidden="true" />
                    ) : null}
                  </div>
                  {reconnectNudge ? (
                    <p className="text-sm">
                      Novos recursos requerem reconectar essa conta para habilitá-los.
                    </p>
                  ) : meta.status === 'error' || meta.status === 'revoked' ? (
                    <p className="text-destructive text-sm">
                      A conexão precisa de atenção. Reconecte para continuar usando essa conta como
                      fonte de dados.
                    </p>
                  ) : null}
                  <div className="flex flex-wrap gap-2">
                    {needsAttention ? (
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => void handleReconnect(slotId)}
                        disabled={pending !== null}
                      >
                        {pending === reconnectAction ? (
                          <Loader className="size-4 animate-spin" />
                        ) : (
                          <Mark />
                        )}
                        Reconectar
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void handleDisconnect(slotId, title)}
                      disabled={pending !== null}
                    >
                      {pending === slotId ? (
                        <Loader className="size-4 animate-spin" />
                      ) : (
                        <Unlink className="size-4" />
                      )}
                      Desvincular
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {!googleActive && googleAccountFeatureOn ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => void handleConnectGoogle()}
              disabled={pending !== null}
            >
              {pending === 'connect-google' ? (
                <Loader className="size-4 animate-spin" />
              ) : (
                <GoogleMark />
              )}
              Conectar conta Google
            </Button>
          ) : null}
          {msExtraSlotAvailable && msExtraAccountsFeatureOn ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => void handleConnectMsExtra()}
              disabled={pending !== null}
            >
              {pending === 'connect-ms-extra' ? (
                <Loader className="size-4 animate-spin" />
              ) : (
                <MicrosoftMark />
              )}
              Conectar conta Microsoft adicional
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  )
}

/** Dropdown-picker de preferência (mesmo padrão do mode-toggle). */
function SettingPicker({
  id,
  ariaLabel,
  value,
  options,
  onSelect,
  loadingValue = null,
  disabledValues,
}: {
  id?: string
  ariaLabel: string
  value: string
  options: ReadonlyArray<{ value: string; label: string }>
  onSelect: (value: string) => void
  /** Valor cuja prévia está carregando; exibe um spinner na opção e no gatilho. */
  loadingValue?: string | null
  /** Valores que ficam desabilitados (não selecionáveis) no menu. */
  disabledValues?: readonly string[]
}) {
  const activeLabel = options.find((o) => o.value === value)?.label ?? options[0]?.label ?? ''
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          aria-label={ariaLabel}
          className="w-full justify-between sm:max-w-xs"
        >
          <span className="truncate">{activeLabel}</span>
          {loadingValue ? (
            <Loader className="size-4 shrink-0 animate-spin opacity-70" />
          ) : (
            <ChevronDown className="size-4 shrink-0 opacity-70" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[--radix-dropdown-menu-trigger-width] min-w-56"
      >
        {options.map((option) => {
          const disabled = disabledValues?.includes(option.value) === true
          return (
            <DropdownMenuItem
              key={option.value}
              disabled={disabled}
              onSelect={() => {
                if (disabled) return
                onSelect(option.value)
              }}
              className="justify-between"
            >
              {option.label}
              {option.value === loadingValue ? (
                <Loader className="size-4 animate-spin" />
              ) : option.value === value ? (
                <Check className="size-4" />
              ) : null}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function VoiceCard({
  loading,
  preferences,
}: {
  loading: boolean
  preferences: UserPreferences | undefined
}) {
  const { user } = useAuth()
  const uid = user?.uid ?? null

  const current = preferences ?? DEFAULT_PREFERENCES
  const language: AgentLanguage = current.language === 'en-US' ? 'en-US' : 'pt-BR'
  const voiceName = current.voiceName ?? DEFAULT_LIVE_VOICE
  const liveModel = current.liveModel ?? LIVE_MODEL_OPTIONS[0].id
  const persona = getPersonaForVoice(voiceName)
  const personaArtigo = persona.gender === 'feminina' ? 'da' : 'do'

  // Preview de áudio da voz selecionada (amostras estáticas em /voices).
  const previewRef = useRef<HTMLAudioElement | null>(null)
  // Voz cuja prévia está carregando/iniciando (null quando nada está pendente).
  const [previewLoading, setPreviewLoading] = useState<string | null>(null)

  function playVoicePreview(name: string) {
    // Descarta a prévia anterior: pausa e solta os handlers para que um play()
    // que resolva tarde não toque nem mexa no estado depois da troca de voz.
    const previous = previewRef.current
    if (previous) {
      previous.pause()
      previous.onplaying = null
      previous.onended = null
      previous.onerror = null
    }

    const audio = new Audio(`/voices/${name.toLowerCase()}.wav`)
    previewRef.current = audio
    setPreviewLoading(name)

    // Só reage se este ainda for o áudio atual — evita a corrida ao trocar rápido.
    const finish = () => {
      if (previewRef.current === audio) setPreviewLoading(null)
    }
    audio.onplaying = finish
    audio.onended = finish
    audio.onerror = finish

    // Reprodução pode ser bloqueada (autoplay policy) — limpa o loading mesmo assim.
    void audio.play().catch(finish)
  }

  // Ao sair da rota, interrompe qualquer prévia em andamento e solta os handlers.
  useEffect(() => {
    return () => {
      const audio = previewRef.current
      if (audio) {
        audio.pause()
        audio.onplaying = null
        audio.onended = null
        audio.onerror = null
      }
      previewRef.current = null
    }
  }, [])

  async function persist(patch: Partial<UserPreferences>) {
    if (!uid) return
    try {
      await updateUserPreferences(uid, patch)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível salvar', { description: message })
    }
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Voz e idioma</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Voz e idioma</CardTitle>
        <CardDescription>
          Idioma da conversa e, na conversa por voz, a voz {personaArtigo} {persona.name} e o modelo
          usado.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="language">Idioma</Label>
          <SettingPicker
            id="language"
            ariaLabel="Idioma da conversa"
            value={language}
            options={LANGUAGE_OPTIONS}
            onSelect={(value) => void persist({ language: value as AgentLanguage })}
          />
          <p className="text-muted-foreground text-xs">
            Vale para o chat de texto e para a voz; sessões de voz novas usam o idioma escolhido.
          </p>
        </div>

        {VOICE_ENABLED ? (
          <>
            <Separator />

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="voice">Voz</Label>
              <SettingPicker
                id="voice"
                ariaLabel="Voz da conversa por voz"
                value={voiceName}
                options={LIVE_VOICE_OPTIONS.map((v) => ({ value: v.name, label: v.label }))}
                onSelect={(value) => {
                  playVoicePreview(value)
                  void persist({ voiceName: value })
                }}
                loadingValue={previewLoading}
              />
              <p className="text-muted-foreground text-xs">
                Aplicada às próximas sessões de voz. Ao selecionar, uma prévia da voz é reproduzida.
              </p>
            </div>

            <Separator />

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="live-model">Modelo de voz</Label>
              <SettingPicker
                id="live-model"
                ariaLabel="Modelo da conversa por voz"
                value={liveModel}
                options={LIVE_MODEL_OPTIONS.map((m) => ({ value: m.id, label: m.label }))}
                onSelect={(value) => void persist({ liveModel: value })}
              />
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  )
}

function MemoriesCard({
  loading,
  preferences,
}: {
  loading: boolean
  preferences: UserPreferences | undefined
}) {
  const { user } = useAuth()
  const uid = user?.uid ?? null

  // Ausente ⇒ aprovação obrigatória (default ON, sem migração de dados).
  const requireApproval = memoriesRequireApproval(preferences)

  async function persist(next: boolean) {
    if (!uid) return
    try {
      // Mapa aninhado enviado inteiro — o merge do Firestore é raso (mesma regra
      // de `quietHours`/`routines`).
      await updateUserPreferences(uid, { memories: { requireApproval: next } })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível salvar', { description: message })
    }
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Memórias</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Memórias</CardTitle>
        <CardDescription>
          O agente aprende fatos das suas conversas e os consolida uma vez por dia. Gerencie-os na
          página Memórias.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-start justify-between gap-4">
          <Label htmlFor="memories-require-approval" className="flex flex-col items-start gap-0.5">
            <span>Aprovar novas memórias antes de usar</span>
            <span className="text-muted-foreground text-xs font-normal">
              Com esta opção ativa, memórias extraídas das conversas ficam pendentes até você
              aprovar na página Memórias.
            </span>
          </Label>
          <Switch
            id="memories-require-approval"
            checked={requireApproval}
            onCheckedChange={(checked) => void persist(checked)}
          />
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Textarea controlada para a instrução de uma rotina. Mantém um rascunho local
 * e só persiste no `onBlur` (não a cada tecla), ressincronizando quando o valor
 * salvo muda (ex.: primeira carga do documento).
 */
function BriefingInstructionField({
  fieldKey,
  label,
  placeholder,
  value,
  onSave,
}: {
  fieldKey: BriefingRoutineKey
  label: string
  placeholder: string
  value: string
  onSave: (value: string) => void
}) {
  const [draft, setDraft] = useState(value)

  useEffect(() => {
    setDraft(value)
  }, [value])

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={`briefing-instructions-${fieldKey}`}>{label}</Label>
      <Textarea
        id={`briefing-instructions-${fieldKey}`}
        maxLength={500}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => onSave(draft)}
        placeholder={placeholder}
      />
    </div>
  )
}

function PreferencesCard({
  loading,
  preferences,
  microsoftConnected,
  microsoft,
  whatsapp,
  features,
}: {
  loading: boolean
  preferences: UserPreferences | undefined
  microsoftConnected: boolean
  microsoft: MicrosoftIntegrationMeta | undefined
  whatsapp: UserDoc['whatsapp']
  /** Feature por usuário (liberação gradual — ver UserFeatureFlags); ausente ⇒ nenhuma habilitada. */
  features: UserFeatureFlags | undefined
}) {
  const whatsappFeatureOn = features?.whatsapp === true
  const { user, connectMicrosoft } = useAuth()
  const uid = user?.uid ?? null
  const [connecting, setConnecting] = useState(false)

  // Local draft mirrors the persisted prefs; each control writes through on change.
  const current = preferences ?? DEFAULT_PREFERENCES
  const routines = { ...DEFAULT_PREFERENCES.routines, ...current.routines }
  const quietHours: { start: string; end: string } = {
    start: current.quietHours?.start ?? DEFAULT_PREFERENCES.quietHours?.start ?? '21:00',
    end: current.quietHours?.end ?? DEFAULT_PREFERENCES.quietHours?.end ?? '07:00',
  }
  const timezone = current.timezone || DEFAULT_TIMEZONE
  const briefingInstructions = current.briefingInstructions ?? {}
  // Flags independentes por canal (Teams/e-mail/WhatsApp), com fallback para o
  // campo legado `deliveryChannel` — ver resolveChannels em @agente/shared.
  const channels = resolveChannels(current)
  const whatsappLinked = whatsapp?.contactId != null

  // E-mail exige o escopo Mail.Send: bloqueado se o token está defasado
  // (reconnectRequired) ou se Mail.Send não está entre os escopos concedidos.
  // Terceiro caso: conta conectada mas SEM nenhum escopo registrado — usuários
  // que conectaram antes da persistência de escopos existir. missingRequiredScopes
  // devolve [] nesse caso (guard anti-falso-positivo) e o script de reconciliação
  // os pula, então reconnectRequired fica false; sem este gate o e-mail pareceria
  // disponível e a chamada real falharia com 403 sem aviso. Tratamos como
  // indisponível e pedimos reconexão.
  const scopesUnknown =
    microsoft?.connected === true && (!microsoft?.scopes || microsoft.scopes.length === 0)
  const emailDisabled =
    microsoft?.reconnectRequired === true ||
    scopesUnknown ||
    missingRequiredScopes(microsoft?.scopes, ['Mail.Send']).length > 0

  async function handleConnect() {
    setConnecting(true)
    try {
      await connectMicrosoft()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível iniciar a conexão', { description: message })
      setConnecting(false)
    }
  }

  async function persist(patch: Partial<UserPreferences>) {
    if (!uid) return
    try {
      await updateUserPreferences(uid, patch)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível salvar', { description: message })
    }
  }

  function toggleRoutine(key: RoutineType, enabled: boolean) {
    void persist({ routines: { ...routines, [key]: enabled } })
  }

  function setChannel(channel: 'teams' | 'email' | 'whatsapp', enabled: boolean) {
    // Grava o mapa inteiro — merge do Firestore é raso (mesmo padrão dos demais
    // toggles), então as três chaves precisam ir sempre juntas.
    void persist({ deliveryChannels: { ...channels, [channel]: enabled } })
  }

  function setBriefingInstruction(key: BriefingRoutineKey, value: string) {
    // Envia o mapa inteiro por causa do merge raso do Firestore (mesmo padrão dos toggles).
    void persist({ briefingInstructions: { ...briefingInstructions, [key]: value } })
  }

  function setQuietHour(edge: 'start' | 'end', value: string) {
    void persist({ quietHours: { ...quietHours, [edge]: value } })
  }

  function setTimezone(value: string) {
    void persist({ timezone: value.trim() || DEFAULT_TIMEZONE })
  }

  const [whatsappPhoneDraft, setWhatsappPhoneDraft] = useState('')
  const [whatsappPending, setWhatsappPending] = useState(false)

  async function handleLinkWhatsapp() {
    const phone = whatsappPhoneDraft.trim()
    if (!phone) {
      toast.error('Informe o número de WhatsApp.')
      return
    }
    setWhatsappPending(true)
    try {
      await linkWhatsapp({ phoneE164: phone })
      toast.success('WhatsApp vinculado')
      setWhatsappPhoneDraft('')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível vincular o WhatsApp', { description: message })
    } finally {
      setWhatsappPending(false)
    }
  }

  async function handleUnlinkWhatsapp() {
    setWhatsappPending(true)
    try {
      await unlinkWhatsapp()
      toast.success('WhatsApp desvinculado')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível desvincular o WhatsApp', { description: message })
    } finally {
      setWhatsappPending(false)
    }
  }

  // Teste avulso de canal (sendChannelTest): só um por vez, indicado pelo
  // canal em `channelTestPending` (spinner + disabled no botão clicado).
  const [channelTestPending, setChannelTestPending] = useState<
    'teams' | 'email' | 'whatsapp' | null
  >(null)

  async function handleSendChannelTest(channel: 'teams' | 'email' | 'whatsapp') {
    setChannelTestPending(channel)
    try {
      const { data } = await sendChannelTest({ channel })
      if (data.delivered) {
        const description =
          channel === 'whatsapp'
            ? data.via === 'sendText'
              ? 'Enviado por mensagem de texto livre (janela de 24h aberta).'
              : 'Enviado pelo template aprovado (janela de 24h fechada).'
            : undefined
        toast.success('Teste enviado', { description })
      } else {
        toast.error('Não foi possível enviar o teste', { description: data.error })
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível enviar o teste', { description: message })
    } finally {
      setChannelTestPending(null)
    }
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Preferências</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Preferências</CardTitle>
        <CardDescription>Rotinas proativas, horário de silêncio e fuso horário.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <fieldset className="flex flex-col gap-4">
          <legend className="text-sm font-medium">Rotinas</legend>
          {!microsoftConnected ? (
            <p className="text-muted-foreground text-sm">
              As rotinas só são disparadas após conectar o Microsoft 365.
            </p>
          ) : null}
          {ROUTINE_LABELS.map(({ key, title, hint }) => (
            <div key={key} className="flex items-start justify-between gap-4">
              <Label htmlFor={`routine-${key}`} className="flex flex-col items-start gap-0.5">
                <span>{title}</span>
                <span className="text-muted-foreground text-xs font-normal">{hint}</span>
              </Label>
              <Switch
                id={`routine-${key}`}
                checked={routines[key] === true}
                onCheckedChange={(checked) => toggleRoutine(key, checked)}
              />
            </div>
          ))}
        </fieldset>

        <Separator />

        <fieldset className="flex flex-col gap-4">
          <legend className="text-sm font-medium">Canais de entrega</legend>
          <p className="text-muted-foreground text-xs">
            Onde os resumos e lembretes proativos são entregues. Pode combinar mais de um canal.
          </p>

          <div className="flex items-start justify-between gap-4">
            <Label htmlFor="channel-teams" className="flex flex-col items-start gap-0.5">
              <span>Teams</span>
            </Label>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void handleSendChannelTest('teams')}
                disabled={!microsoftConnected || channelTestPending === 'teams'}
              >
                {channelTestPending === 'teams' ? (
                  <Loader className="size-4 animate-spin" />
                ) : (
                  <Send className="size-4" />
                )}
                Enviar teste
              </Button>
              <Switch
                id="channel-teams"
                checked={channels.teams}
                onCheckedChange={(checked) => setChannel('teams', checked)}
              />
            </div>
          </div>

          <div className="flex items-start justify-between gap-4">
            <Label htmlFor="channel-email" className="flex flex-col items-start gap-0.5">
              <span>E-mail</span>
              {emailDisabled ? (
                <span className="text-muted-foreground text-xs font-normal">
                  Requer reconectar o Microsoft 365 (escopo Mail.Send).
                </span>
              ) : null}
            </Label>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void handleSendChannelTest('email')}
                disabled={emailDisabled || channelTestPending === 'email'}
              >
                {channelTestPending === 'email' ? (
                  <Loader className="size-4 animate-spin" />
                ) : (
                  <Send className="size-4" />
                )}
                Enviar teste
              </Button>
              <Switch
                id="channel-email"
                checked={channels.email}
                disabled={emailDisabled}
                onCheckedChange={(checked) => setChannel('email', checked)}
              />
            </div>
          </div>
          {emailDisabled ? (
            <div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void handleConnect()}
                disabled={connecting}
              >
                {connecting ? <Loader className="size-4 animate-spin" /> : <MicrosoftMark />}
                Reconectar Microsoft 365
              </Button>
            </div>
          ) : null}

          {whatsappFeatureOn || whatsappLinked ? (
            <>
              <div className="flex items-start justify-between gap-4">
                <Label htmlFor="channel-whatsapp" className="flex flex-col items-start gap-0.5">
                  <span>WhatsApp</span>
                  {!whatsappLinked ? (
                    <span className="text-muted-foreground text-xs font-normal">
                      Vincule um número de WhatsApp abaixo para habilitar este canal.
                    </span>
                  ) : null}
                </Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void handleSendChannelTest('whatsapp')}
                    disabled={!whatsappLinked || channelTestPending === 'whatsapp'}
                  >
                    {channelTestPending === 'whatsapp' ? (
                      <Loader className="size-4 animate-spin" />
                    ) : (
                      <Send className="size-4" />
                    )}
                    Enviar teste
                  </Button>
                  <Switch
                    id="channel-whatsapp"
                    checked={channels.whatsapp}
                    disabled={!whatsappLinked}
                    onCheckedChange={(checked) => setChannel('whatsapp', checked)}
                  />
                </div>
              </div>

              <div className="border-border bg-muted/40 flex flex-col gap-3 rounded-lg border p-3">
                {whatsappLinked ? (
                  <>
                    <div>
                      <p className="text-sm font-medium">Número vinculado</p>
                      <p className="text-muted-foreground truncate text-sm">
                        {whatsapp?.phoneE164}
                      </p>
                    </div>
                    <div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => void handleUnlinkWhatsapp()}
                        disabled={whatsappPending}
                      >
                        {whatsappPending ? (
                          <Loader className="size-4 animate-spin" />
                        ) : (
                          <Unlink className="size-4" />
                        )}
                        Desvincular
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <Label htmlFor="whatsapp-phone" className="text-sm font-medium">
                      Número de WhatsApp
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      <Input
                        id="whatsapp-phone"
                        className="max-w-56"
                        value={whatsappPhoneDraft}
                        onChange={(e) => setWhatsappPhoneDraft(e.target.value)}
                        placeholder="+55 11 98765-4321"
                        disabled={whatsappPending}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => void handleLinkWhatsapp()}
                        disabled={whatsappPending}
                      >
                        {whatsappPending ? (
                          <Loader className="size-4 animate-spin" />
                        ) : (
                          <Link2 className="size-4" />
                        )}
                        Vincular
                      </Button>
                    </div>
                    <p className="text-muted-foreground text-xs">
                      DDD + número (ex.: 11987654321) ou já no formato internacional.
                    </p>
                  </>
                )}
              </div>

              <p className="text-muted-foreground text-xs">
                Com um número vinculado, você também pode conversar com o agente pelo WhatsApp — por
                texto, áudio ou anexos, bastando mandar mensagem para o número da firma. As
                mensagens aparecem aqui, na conversa "WhatsApp".
              </p>
              {whatsapp?.conversationId ? (
                <Button type="button" variant="outline" size="sm" className="w-fit" asChild>
                  <Link to="/conversations/$id" params={{ id: whatsapp.conversationId }}>
                    <MessageCircle className="size-4" />
                    Abrir conversa do WhatsApp
                  </Link>
                </Button>
              ) : null}
            </>
          ) : null}
        </fieldset>

        <Separator />

        <fieldset className="flex flex-col gap-4">
          <legend className="text-sm font-medium">Instruções personalizadas</legend>
          <p className="text-muted-foreground text-xs">
            Ajustes de tom e de apresentação aplicados quando cada resumo é gerado. Não substituem
            os compromissos reais da agenda nem autorizam inventar dados.
          </p>
          {BRIEFING_INSTRUCTION_LABELS.map(({ key, label, placeholder }) => (
            <BriefingInstructionField
              key={key}
              fieldKey={key}
              label={label}
              placeholder={placeholder}
              value={briefingInstructions[key] ?? ''}
              onSave={(value) => setBriefingInstruction(key, value)}
            />
          ))}
        </fieldset>

        <Separator />

        <fieldset className="flex flex-col gap-3">
          <legend className="text-sm font-medium">Horário de silêncio</legend>
          <p className="text-muted-foreground text-xs">
            Período em que os avisos no Teams ficam suspensos.
          </p>
          <div className="flex flex-wrap gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="quiet-start">Início</Label>
              <Input
                id="quiet-start"
                type="time"
                className="w-32"
                value={quietHours?.start ?? ''}
                onChange={(e) => setQuietHour('start', e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="quiet-end">Fim</Label>
              <Input
                id="quiet-end"
                type="time"
                className="w-32"
                value={quietHours?.end ?? ''}
                onChange={(e) => setQuietHour('end', e.target.value)}
              />
            </div>
          </div>
        </fieldset>

        <Separator />

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="timezone">Fuso horário</Label>
          <Input
            id="timezone"
            className="max-w-xs"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            placeholder={DEFAULT_TIMEZONE}
          />
          <p className="text-muted-foreground text-xs">
            Identificador IANA, por exemplo {DEFAULT_TIMEZONE}.
          </p>
        </div>
      </CardContent>
    </Card>
  )
}

export default SettingsRoute
